import React from 'react';
import Helmet from 'react-helmet';

import { useIntl } from 'ha/i18n';
import { useLocation } from 'ha/helpers/router';
import { CanonicalCollections } from 'ha/constants/SearchPageCollections';

interface Props {
  canonicalCollection: string;
  cityName: string;
  countryName: string;
  currency: string;
  priceMax: number;
  priceMin: number;
  priceAverage: number;
  advertiserName?: string;
  total: number;
  latitude: number;
  longitude: number;
}

const SearchMeta: React.FC<Props> = ({
  cityName,
  countryName,
  canonicalCollection,
  currency,
  priceMax: noCentsPriceMax,
  priceMin: noCentsPriceMin,
  priceAverage: noCentsPriceAverage,
  advertiserName,
  total,
  latitude,
  longitude,
}) => {
  const { T } = useIntl();
  const {
    query: { page },
  } = useLocation();

  const priceMax = noCentsPriceMax / 100;
  const priceMin = noCentsPriceMin / 100;
  const priceAverage = Math.floor(noCentsPriceAverage / 100);

  const title = advertiserName
    ? T('seo_search_meta_title_advertiser_page', advertiserName, cityName)
    : {
        [CanonicalCollections.ApartmentForRent]: T(
          'seo_search_meta_title_apartment_for_rent',
          cityName,
        ),
        [CanonicalCollections.PrivateRoomForRent]: T(
          'seo_search_meta_title_private_room_for_rent',
          cityName,
        ),
        [CanonicalCollections.SharedRoomForRent]: T(
          'seo_search_meta_title_shared_room_for_rent',
          cityName,
        ),
        [CanonicalCollections.StudioForRent]: T(
          'seo_search_meta_title_studio_for_rent',
          cityName,
        ),
        [CanonicalCollections.CheapAccommodation]: T(
          'seo_search_meta_title_cheap_accommodation',
          cityName,
        ),
        [CanonicalCollections.FurnishedApartments]: T(
          'seo_search_meta_title_furnished_apartments',
          cityName,
        ),
        [CanonicalCollections.StudentAccommodation]: T(
          'seo_search_meta_title_student_accommodation',
          cityName,
        ),
        [CanonicalCollections.LongTermRentals]: T(
          'seo_search_meta_title_long_term_rentals',
          cityName,
        ),
        [CanonicalCollections.OneBedroomApartments]: T(
          'seo_search_meta_title_one_bedroom_apartments',
          cityName,
        ),
        [CanonicalCollections.TwoBedroomApartments]: T(
          'seo_search_meta_title_two_bedroom_apartments',
          cityName,
        ),
        [CanonicalCollections.StudentRoom]: T(
          'seo_search_meta_title_student_room',
          cityName,
        ),
        '': T('seo_search_meta_title', cityName, countryName),
      }[canonicalCollection];

  const description = advertiserName
    ? T(
        'seo_search_meta_description_advertiser_page',
        advertiserName,
        cityName,
        currency,
        priceMin,
      )
    : {
        [CanonicalCollections.ApartmentForRent]: T(
          'seo_search_meta_description_apartment_for_rent',
          cityName,
          currency,
          priceMin,
        ),
        [CanonicalCollections.PrivateRoomForRent]: T(
          'seo_search_meta_description_private_room_for_rent',
          cityName,
          currency,
          priceMin,
        ),
        [CanonicalCollections.SharedRoomForRent]: T(
          'seo_search_meta_description_shared_room_for_rent',
          cityName,
          currency,
          priceMin,
        ),
        [CanonicalCollections.StudioForRent]: T(
          'seo_search_meta_description_studio_for_rent',
          cityName,
        ),
        [CanonicalCollections.CheapAccommodation]: T(
          'seo_search_meta_description_cheap_accommodation',
          cityName,
          currency,
          priceMin,
        ),
        [CanonicalCollections.FurnishedApartments]: T(
          'seo_search_meta_description_furnished_apartments',
          cityName,
          currency,
          priceMin,
        ),
        [CanonicalCollections.StudentAccommodation]: T(
          'seo_search_meta_description_student_accommodation',
          cityName,
          currency,
          priceMin,
        ),
        [CanonicalCollections.LongTermRentals]: T(
          'seo_search_meta_description_long_term_rentals',
          cityName,
        ),
        [CanonicalCollections.OneBedroomApartments]: T(
          'seo_search_meta_description_one_bedroom_apartments',
          cityName,
          currency,
          priceMin,
        ),
        [CanonicalCollections.TwoBedroomApartments]: T(
          'seo_search_meta_description_two_bedroom_apartments',
          cityName,
          currency,
          priceMin,
        ),
        [CanonicalCollections.StudentRoom]: T(
          'seo_search_meta_description_student_room',
          cityName,
          currency,
          priceMin,
        ),
        '': T('seo_search_meta_description', cityName, currency, priceMin),
      }[canonicalCollection];

  const pageSuffix = page ? ` | ${T('Page %s', page)}` : '';

  const suffixedTitle = `${title}${pageSuffix}`;

  const isCheapAccommodation =
    canonicalCollection === CanonicalCollections.CheapAccommodation;

  const structuredData = {
    '@context': 'http://schema.org',
    '@type': ['Apartment', 'Product'],
    name: suffixedTitle,
    description,
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: currency,
      offerCount: total,
      highPrice: isCheapAccommodation ? priceAverage : priceMax,
      lowPrice: priceMin,
    },
    hasMap: `http://www.google.com/maps/place/${latitude},${longitude}`,
  };

  return (
    <Helmet titleTemplate="%s | HousingAnywhere">
      <title>{suffixedTitle}</title>
      <meta property="og:title" content={suffixedTitle} />

      <meta name="description" content={description} />
      <meta property="og:description" content={description} />

      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SearchMeta;
